import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import { useListEquipmentTypes } from "#hooks/adapters/useEquipment";
import {
  EquipmentDropdownInput,
  FacilityDropdownInput,
} from "#src/batteries-included-components";
import useLocalization from "#src/hooks/useLocalization";
import {
  DateSelectorInput,
  DropdownInput,
  TextInput,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentSchema,
  FacilitySchema,
  getSchemaPropertyReference,
} from "@validereinc/domain";
import { Equipment, Facilities } from "@validereinc/domain-controllers";
import React from "react";

const { EquipmentStatusOptions } = Equipment.view;
const { FacilityStatusOptions } = Facilities.view;

const EquipmentKeys = {
  name: getSchemaPropertyReference(EquipmentSchema, "name"),
  id: getSchemaPropertyReference(EquipmentSchema, "id"),
  type: getSchemaPropertyReference(EquipmentSchema, "type"),
  status: getSchemaPropertyReference(EquipmentSchema, "status"),
};

/** Get the FilterPanel Filters for querying the Equipment Search API */
export const useEquipmentFilters = ({
  isWithCustomAttributes = false,
  isWithPeriod = false,
}: {
  isWithCustomAttributes?: boolean;
  isWithPeriod?: boolean;
} = {}) => {
  const { localize } = useLocalization();
  const { customAttributeFilters } = useCustomAttributeFilters({
    assetType: AssetType.EQUIPMENT,
    enabled: isWithCustomAttributes,
  });

  const equipmentTypes = useListEquipmentTypes({}).data?.data ?? [];

  const periodFilter = {
    component: (
      <DateSelectorInput
        key={"period"}
        name={"period"}
        variant="month"
        isInline
      />
    ),
  };

  return [
    {
      component: (
        <TextInput
          key={EquipmentKeys.name}
          name={EquipmentKeys.name}
          placeholder={`Search ${localize("equipment_plural")}...`}
          type="search"
          isInline
        />
      ),
    },
    ...(isWithPeriod ? [periodFilter] : []),
    {
      section: "Filters",
      component: (
        <EquipmentDropdownInput
          key={EquipmentKeys.id}
          name={EquipmentKeys.name}
          isMulti
          isFluid
          isOptionalTextShown={false}
        />
      ),
    },
    {
      section: "Filters",
      component: (
        <DropdownInput
          key={EquipmentKeys.type}
          name={EquipmentKeys.type}
          label="Type"
          options={equipmentTypes}
          labelKey="name"
          valueKey="id"
          isMulti
          isFluid
          isSearchable
          isOptionalTextShown={false}
        />
      ),
    },
    {
      section: "Filters",
      component: (
        <DropdownInput
          key={EquipmentKeys.status}
          name={EquipmentKeys.status}
          label="Status"
          options={EquipmentStatusOptions}
          labelKey="label"
          valueKey="value"
          isMulti
          isFluid
          isSearchable
          isOptionalTextShown={false}
        />
      ),
    },
    ...(isWithCustomAttributes ? customAttributeFilters : []),
  ];
};

export const EQUIPMENT_FILTERS = {
  equipmentName: {
    name: getSchemaPropertyReference(EquipmentSchema, "id", ["equipment"]),
    label: "Name",
  },
  equipmentType: {
    name: getSchemaPropertyReference(EquipmentSchema, "type.id", ["equipment"]),
    label: "Type",
  },
  equipmentStatus: {
    name: getSchemaPropertyReference(EquipmentSchema, "status", ["equipment"]),
    label: "Status",
  },
  facilityName: {
    name: getSchemaPropertyReference(FacilitySchema, "id", [
      "equipment",
      "facility",
    ]),
    label: "Name",
  },
  facilityStatus: {
    name: getSchemaPropertyReference(FacilitySchema, "status", [
      "equipment",
      "facility",
    ]),
    label: "Status",
  },
};

/** Equipment filters for querying the Calculator Configuration Search API */
export const useEquipmentCalculationFilters = () => {
  const { localize } = useLocalization();

  const localizedEquipmentLabel = localize("Equipment");
  const localizedFacilityLabel = localize("Facility");

  const equipmentTypes = useListEquipmentTypes({}).data?.data ?? [];

  const { customAttributeFilters: facilityCustomAttributeFilters } =
    useCustomAttributeFilters({
      assetType: AssetType.FACILITY,
      section: localizedFacilityLabel,
      prefix: "equipment.facility.custom_attributes",
    });

  const { customAttributeFilters: equipmentCustomAttributeFilters } =
    useCustomAttributeFilters({
      assetType: AssetType.EQUIPMENT,
      section: localizedEquipmentLabel,
      prefix: "equipment.custom_attributes",
    });

  return {
    filters: [
      {
        section: localizedEquipmentLabel,
        component: (
          <EquipmentDropdownInput
            key={EQUIPMENT_FILTERS.equipmentName.name}
            name={EQUIPMENT_FILTERS.equipmentName.name}
            label={EQUIPMENT_FILTERS.equipmentName.label}
            isMulti
            isFluid
            isOptionalTextShown={false}
          />
        ),
      },
      {
        section: localizedEquipmentLabel,
        component: (
          <DropdownInput
            key={EQUIPMENT_FILTERS.equipmentType.name}
            name={EQUIPMENT_FILTERS.equipmentType.name}
            label={EQUIPMENT_FILTERS.equipmentType.label}
            options={equipmentTypes}
            labelKey="name"
            valueKey="id"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown={false}
          />
        ),
      },
      {
        section: localizedEquipmentLabel,
        component: (
          <DropdownInput
            key={EQUIPMENT_FILTERS.equipmentStatus.name}
            name={EQUIPMENT_FILTERS.equipmentStatus.name}
            label={EQUIPMENT_FILTERS.equipmentStatus.label}
            options={EquipmentStatusOptions}
            labelKey="label"
            valueKey="value"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown={false}
          />
        ),
      },
      {
        section: localizedFacilityLabel,
        component: (
          <FacilityDropdownInput
            key={EQUIPMENT_FILTERS.facilityName.name}
            name={EQUIPMENT_FILTERS.facilityName.name}
            label={EQUIPMENT_FILTERS.facilityName.label}
            isMulti
            isFluid
            isOptionalTextShown={false}
          />
        ),
      },
      {
        section: localizedFacilityLabel,
        component: (
          <DropdownInput
            key={EQUIPMENT_FILTERS.facilityStatus.name}
            name={EQUIPMENT_FILTERS.facilityStatus.name}
            label={EQUIPMENT_FILTERS.facilityStatus.label}
            options={FacilityStatusOptions}
            labelKey="label"
            valueKey="value"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown={false}
          />
        ),
      },
      ...(facilityCustomAttributeFilters ?? []),
      ...(equipmentCustomAttributeFilters ?? []),
    ],
    filterKeys: [localizedEquipmentLabel, localizedFacilityLabel],
  };
};
